import { differenceInCalendarDays, format, isToday, parseISO } from "date-fns";
import { mapLogisticsTicketToPipelinePickup } from "../Pipeline/mapLogisticsTicketToPipelinePickup";
import { allowsManualStageChange, NEXT_STAGE, PREVIOUS_STAGE, PIPELINE_COLUMNS } from "../Pipeline/pipelineColumns";
import { LogisticsTicket, LogisticsTicketDestinationAddress, LogisticsTicketQuoteRequest } from "types/logisticsTicket";
import { PickupContact } from "types/pickupContact";
import {
  JobDetailAssignment,
  JobDetailConfirmation,
  JobDetailCountSheetItem,
  JobDetailDeliveryVerification,
  JobDetailPickupCompletion,
  JobDetailVendor,
  JobDetailViewModel,
} from "./jobDetailTypes";
import { Pickup } from "types/pickup";
import { logisticsTeam } from "constants/logisticsTeam";
import { formatCentsAmount } from "utils/currency";

const VENDOR_COLORS = ["#1E88E5", "#43A047", "#FB8C00", "#8E24AA", "#546E7A"];

function getMainContact(contacts: PickupContact[]) {
  return contacts.find((contact) => contact.pointOfContactType === "main") ?? contacts[0];
}

function formatPickupDate(dateStr?: string) {
  if (!dateStr) {
    return "—";
  }

  const date = parseISO(dateStr.split("T")[0]);
  return format(date, "yyyy-MM-dd");
}

function formatTimelineStamp(value?: string) {
  if (!value) {
    return undefined;
  }

  try {
    return format(parseISO(value), "MMM d · h:mm a");
  } catch {
    return undefined;
  }
}

function buildAssetSummary(ticket: LogisticsTicket): string {
  const items = ticket.pickup.pickupEstimatedItems;
  if (items.length === 0) {
    return "—";
  }

  return items.map((item) => `${item.quantity} ${item.name}`).join(", ");
}

function formatPrice(amountInCents: number, vendorName?: string): string {
  if (amountInCents === 0 || vendorName?.toLowerCase().includes("revivn")) {
    return "$0";
  }

  return formatCentsAmount(amountInCents);
}

function vendorColor(index: number) {
  return VENDOR_COLORS[index % VENDOR_COLORS.length];
}

function getWinningRequest(ticket: LogisticsTicket): LogisticsTicketQuoteRequest | undefined {
  if (!ticket.winningQuote) {
    return undefined;
  }

  return (
    ticket.quoteRequests.find((request) => request.quote?.id === ticket.winningQuote?.id) ??
    ticket.quoteRequests.find((request) => request.assigned)
  );
}

function buildVendors(ticket: LogisticsTicket): JobDetailVendor[] {
  const requests = ticket.quoteRequests.filter((request) => request.vendor);

  if (requests.length === 0) {
    return [];
  }

  return requests.map((request, index) => {
    const vendor = request.vendor!;
    const amountInCents = request.quote?.amountInCents ?? 0;

    return {
      id: vendor.uuid,
      name: vendor.name,
      contactName: vendor.contactName,
      email: vendor.email,
      phone: vendor.phone,
      vendorType: vendor.vendorType,
      priceLabel: formatPrice(amountInCents, vendor.name),
      priceInCents: amountInCents,
      initial: vendor.name.charAt(0).toUpperCase(),
      color: vendorColor(index),
      quoteRequestUuid: request.uuid,
      sentAt: request.sentAt,
    };
  });
}

function buildCurrentAssignment(ticket: LogisticsTicket): JobDetailAssignment | null {
  const winningRequest = getWinningRequest(ticket);
  const vendor = winningRequest?.vendor;
  if (!ticket.winningQuote || !vendor) {
    return null;
  }

  const amountInCents = ticket.winningQuote.amountInCents ?? winningRequest.quote?.amountInCents ?? 0;
  const vendorIndex = ticket.quoteRequests.findIndex((request) => request.vendor?.uuid === vendor.uuid);

  return {
    vendorId: vendor.uuid,
    vendorName: vendor.name,
    vendorContactName: vendor.contactName,
    vendorEmail: vendor.email,
    vendorPhone: vendor.phone,
    vendorType: vendor.vendorType ?? (vendor.name.toLowerCase().includes("revivn") ? "Internal Fleet" : "ITAD Partner"),
    priceLabel: formatPrice(amountInCents, vendor.name),
    priceInCents: amountInCents,
    initial: vendor.name.charAt(0).toUpperCase(),
    color: vendorColor(Math.max(vendorIndex, 0)),
    assignedAt: ticket.assignedAt,
    quoteId: ticket.winningQuote.id,
    proposedDate: winningRequest?.quote?.proposedDate,
    vendorAcceptedAt: formatTimelineStamp(winningRequest?.vendorConfirmationAcceptedAt),
    customerConfirmedAt: formatTimelineStamp(winningRequest?.customerAcceptedAt ?? ticket.scheduledAt),
    vendorPortalUrl: winningRequest?.hermesVendorQuoteRequestUrl,
    customerPortalUrl: winningRequest?.hermesCustomerConfirmationUrl,
  };
}

function buildConfirmedForLabel(ticket: LogisticsTicket, winningRequest?: LogisticsTicketQuoteRequest): string {
  const quote = ticket.winningQuote ?? winningRequest?.quote;
  const dateValue =
    quote?.proposedDate ?? ticket.pickup.scheduledAt?.split("T")[0] ?? ticket.pickup.requestedDate?.split("T")[0];
  const timeValue = ticket.pickup.confirmedTime ?? ticket.pickup.requestedTime ?? quote?.proposedTimeWindow;

  if (!dateValue) {
    return "CONFIRMED";
  }

  return timeValue
    ? `CONFIRMED FOR ${formatPickupDate(dateValue)} ${timeValue}`
    : `CONFIRMED FOR ${formatPickupDate(dateValue)}`;
}

function formatRelativeStamp(value?: string) {
  if (!value) {
    return undefined;
  }

  try {
    const date = parseISO(value);
    const timeLabel = format(date, "h:mm a");
    if (isToday(date)) {
      return `today, ${timeLabel}`;
    }
    return `${format(date, "MMM d")}, ${timeLabel}`;
  } catch {
    return undefined;
  }
}

function toCount(value?: string | number | null): number {
  if (value == null || value === "") {
    return 0;
  }

  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function estimateQuantityForCategory(items: Pickup["pickupEstimatedItems"], keywords: string[]) {
  return items.reduce((sum, item) => {
    const name = item.name.toLowerCase();
    if (keywords.some((keyword) => name.includes(keyword))) {
      return sum + (item.quantity || 0);
    }
    return sum;
  }, 0);
}

function buildCountSheetItems(ticket: LogisticsTicket): JobDetailCountSheetItem[] {
  const estimates = ticket.pickup.pickupEstimatedItems || [];
  const form = ticket.pickup.postPickupForms?.[0];

  const categories = [
    {
      label: "MAC",
      estimated: estimateQuantityForCategory(estimates, ["mac", "apple", "imac"]),
      verified:
        toCount(form?.appleLaptopCount) +
        toCount(form?.appleDesktopCount) +
        toCount(form?.macMiniCount) +
        toCount(form?.imacsCount),
    },
    {
      label: "PC",
      estimated: estimateQuantityForCategory(estimates, ["pc", "windows", "desktop"]),
      verified: toCount(form?.pcLaptopCount) + toCount(form?.pcDesktopCount),
    },
    {
      label: "MONITOR",
      estimated: estimateQuantityForCategory(estimates, ["monitor", "display", "thunderbolt"]),
      verified: toCount(form?.monitorsCount) + toCount(form?.thunderboltCount),
    },
  ];

  const hasVerified = Boolean(form);
  const hasAnyData = categories.some((category) => category.estimated > 0 || category.verified > 0);

  if (!hasAnyData) {
    return estimates.slice(0, 3).map((item) => ({
      label: item.name.toUpperCase(),
      verifiedCount: item.quantity || 0,
      estimatedCount: item.quantity || 0,
      delta: 0,
    }));
  }

  return categories
    .filter((category) => category.estimated > 0 || category.verified > 0)
    .map((category) => {
      const verifiedCount = hasVerified ? category.verified : category.estimated;
      return {
        label: category.label,
        verifiedCount,
        estimatedCount: category.estimated,
        delta: verifiedCount - category.estimated,
      };
    });
}

function buildPickupCompletion(
  ticket: LogisticsTicket,
  assignment: JobDetailAssignment | null,
): JobDetailPickupCompletion | null {
  if (ticket.state !== "picked_up" || !assignment) {
    return null;
  }

  const form = ticket.pickup.postPickupForms?.[0];
  const submittedAt = form?.createdAt ?? ticket.startedAt;
  const driverName = form?.fullName || ticket.pickup.pickupAgent?.agent?.name;
  const vendorLabel = assignment.vendorName;

  const submittedParts = [
    submittedAt ? `Submitted ${formatRelativeStamp(submittedAt)}` : null,
    driverName ? `by ${driverName}${vendorLabel ? ` (${vendorLabel} driver)` : ""}` : null,
  ].filter(Boolean);

  return {
    pickedUpOnLabel: `PICKED UP ON ${formatRelativeStamp(ticket.startedAt) ?? "—"}`,
    submittedLabel: submittedParts.length > 0 ? submittedParts.join(" ") : undefined,
    countSheetItems: buildCountSheetItems(ticket),
    finalPriceLabel: `${assignment.priceLabel} final`,
  };
}

function buildDeliveryVerification(
  ticket: LogisticsTicket,
  assignment: JobDetailAssignment | null,
): JobDetailDeliveryVerification | null {
  if (ticket.state !== "delivery_verified" || !assignment) {
    return null;
  }

  return {
    verifiedOnLabel: `DELIVERY VERIFIED ON ${formatRelativeStamp(ticket.completedAt) ?? "—"}`,
    inboundNote: "Container inbounded at Revivn facility.",
    countSheetItems: buildCountSheetItems(ticket),
    pickedUpOnLabel: formatRelativeStamp(ticket.startedAt) ?? "—",
    vendorSummaryLabel: `${assignment.vendorName} (${assignment.vendorType} · ${assignment.priceLabel} final)`,
  };
}

function getDaysSinceReceivedLabel(dateStr?: string): string | null {
  if (!dateStr) {
    return null;
  }

  const days = Math.abs(differenceInCalendarDays(parseISO(dateStr.split("T")[0]), new Date()));
  return String(days);
}

function buildConfirmation(
  ticket: LogisticsTicket,
  assignment: JobDetailAssignment | null,
  contactName: string,
  contactEmail?: string,
  contactPhone?: string,
): JobDetailConfirmation | null {
  if (ticket.state !== "scheduled_confirmed" || !assignment) {
    return null;
  }

  const winningRequest = getWinningRequest(ticket);

  return {
    confirmedForLabel: buildConfirmedForLabel(ticket, winningRequest),
    lockedInPriceLabel: `${assignment.priceLabel} locked-in`,
    vendorAcceptedAt: formatTimelineStamp(
      winningRequest?.vendorConfirmationAcceptedAt ?? winningRequest?.respondedAt ?? ticket.assignedAt,
    ),
    customerConfirmedAt: formatTimelineStamp(winningRequest?.customerAcceptedAt ?? ticket.scheduledAt),
    crewConfirmedAt: formatTimelineStamp(ticket.pickup.vendorCrewConfirmedAt),
    vendorPortalUrl: assignment.vendorPortalUrl,
    customerPortalUrl: assignment.customerPortalUrl,
    crewPortalUrl: winningRequest?.hermesVendorCrewConfirmationUrl,
    vendorEmail: assignment.vendorEmail,
    customerEmail: contactEmail,
    customerPhone: contactPhone,
    customerName: contactName !== "—" ? contactName : undefined,
  };
}

function getDaysFromTodayLabel(dateStr?: string): string | null {
  if (!dateStr) {
    return null;
  }

  const days = differenceInCalendarDays(parseISO(dateStr.split("T")[0]), new Date());
  if (days === 0) {
    return "Today";
  }
  if (days > 0) {
    return `In ${days} day${days === 1 ? "" : "s"}`;
  }

  return `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`;
}

function getStageLabel(stage: JobDetailViewModel["stage"] | undefined): string | null {
  if (!stage) {
    return null;
  }

  const column = PIPELINE_COLUMNS.find((item) => item.stage === stage);
  return column?.title.toUpperCase() ?? null;
}

function getCoordinatorName(ticket: LogisticsTicket) {
  const coordinator = ticket.pickup.revivnCoordinator;
  if (!coordinator) {
    return undefined;
  }

  return [coordinator.firstName, coordinator.lastName].filter(Boolean).join(" ") || undefined;
}

function getDestinationAddresses(ticket: LogisticsTicket): LogisticsTicketDestinationAddress[] {
  const options = [...(ticket.destinationAddresses ?? [])];
  const selected = ticket.destinationAddress;
  if (selected && !options.some((option) => option.id === selected.id)) {
    options.push(selected);
  }
  return options;
}

function getCoordinatorId(ticket: LogisticsTicket, coordinatorName?: string) {
  const coordinator = ticket.pickup.revivnCoordinator;
  if (coordinator?.id != null) {
    return String(coordinator.id);
  }
  if (!coordinatorName) {
    return undefined;
  }

  return logisticsTeam.find(([, name]) => name === coordinatorName)?.[0];
}

export function buildJobDetailViewModel(ticket: LogisticsTicket): JobDetailViewModel | null {
  const pipelinePickup = mapLogisticsTicketToPipelinePickup(ticket);
  if (!pipelinePickup) {
    return null;
  }

  const contact = getMainContact(ticket.pickup.pickupContacts || []);
  const contactName = contact ? [contact.firstName, contact.lastName].filter(Boolean).join(" ") : "—";
  const siteNotes = [
    ticket.pickup.location?.accessInstructions,
    ticket.pickup.location?.internalNotes,
    ticket.pickup.internalNotes,
    ticket.pickup.comments,
  ]
    .filter(Boolean)
    .join(" · ");

  const pickupAttachments = (ticket.pickup.pickupImages || [])
    .filter((attachment) => Boolean(attachment.url))
    .map((attachment, index) => ({
      id: attachment.id,
      label: attachment.filename || `Attachment ${index + 1}`,
      url: attachment.url,
      contentType: attachment.contentType,
    }));

  const certificate = ticket.pickup.certificateOfInsuranceFile;
  if (certificate?.url) {
    pickupAttachments.push({
      id: certificate.id,
      label: certificate.filename || "Certificate of Insurance",
      url: certificate.url,
      contentType: certificate.contentType,
    });
  }

  const requestedDate = ticket.pickup.requestedDate ?? ticket.pickup.scheduledAt;
  const currentAssignment = buildCurrentAssignment(ticket);
  const coordinatorName = getCoordinatorName(ticket);
  const coordinatorId = getCoordinatorId(ticket, coordinatorName);
  const confirmation = buildConfirmation(
    ticket,
    currentAssignment,
    contactName,
    contact?.email,
    contact?.phoneNumber ?? contact?.workPhoneNumber,
  );
  const pickupCompletion = buildPickupCompletion(ticket, currentAssignment);
  const deliveryVerification = buildDeliveryVerification(ticket, currentAssignment);
  const isComplete = pipelinePickup.stage === "delivery_verified";

  return {
    ticketUuid: ticket.uuid,
    displayId: pipelinePickup.displayId,
    companyName: pipelinePickup.companyName,
    city: pipelinePickup.city,
    state: pipelinePickup.state,
    stage: pipelinePickup.stage,
    client: pipelinePickup.companyName,
    location: pipelinePickup.location,
    assets: pipelinePickup.assetCount,
    pallets: pipelinePickup.palletCount,
    pickupDate: formatPickupDate(requestedDate),
    confirmedTime: ticket.pickup.confirmedTime,
    needsAction: ticket.needsAction,
    assetSummary: buildAssetSummary(ticket),
    siteNotes: siteNotes || "—",
    siteInformation: ticket.pickup.siteInformation,
    customerContact: contactName,
    customerContactEmail: contact?.email ?? "—",
    customerContactPhone: contact?.phoneNumber ?? contact?.workPhoneNumber,
    receivedStatus: ticket.completedAt ? (formatRelativeStamp(ticket.completedAt) ?? "—") : "Not received",
    pickupAttachments,
    coordinatorId,
    coordinatorName,
    destinationAddressId: ticket.destinationAddressId ?? null,
    destinationAddresses: getDestinationAddresses(ticket),
    destinationAddressLocked: ticket.destinationAddressLocked === true,
    vendors: buildVendors(ticket),
    currentAssignment,
    confirmation,
    pickupCompletion,
    deliveryVerification,
    daysFromToday: getDaysFromTodayLabel(requestedDate),
    daysSinceReceived: getDaysSinceReceivedLabel(ticket.completedAt),
    invoiceStatus:
      pipelinePickup.stage === "scheduled_confirmed" ||
      pipelinePickup.stage === "picked_up" ||
      pipelinePickup.stage === "delivery_verified"
        ? "missing"
        : "pending",
    isComplete,
    nextStageLabel: allowsManualStageChange(pipelinePickup.stage)
      ? getStageLabel(NEXT_STAGE[pipelinePickup.stage])
      : null,
    previousStageLabel: allowsManualStageChange(pipelinePickup.stage)
      ? getStageLabel(PREVIOUS_STAGE[pipelinePickup.stage])
      : null,
    cancellationReason: pipelinePickup.cancellationReason,
    internalNotes: ticket.internalNotes,
  };
}
